/**
 * @fileoverview Registers **`document`** keydown listener for KorvoChat global shortcuts.
 *
 * @purpose Removes inline **`useEffect`** + local **`handleKeyDown`** from **`KorvoChat.tsx`** for CRAP tooling.
 * @usage **`useKorvoChatDocumentKeydown(ctx, editingMessageId, debugPanelOpen, debugFeatureEnabled, resetConversationFeatureEnabled, showResetConfirm)`** next to other KorvoChat effects.
 * @ownership KorvoChat component infrastructure
 * @dependencies **`react`**, **`./korvo-chat-document-keydown`**
 * @restrictions Pass the same primitives **`KorvoChat`** used for the prior inline **`useEffect`** deps so the listener re-subscribes when routing inputs change.
 */

'use client'

import { useEffect, useRef } from 'react'

import {
  createKorvoChatDocumentKeydownHandler,
  type KorvoChatDocumentKeydownContext,
} from './korvo-chat-document-keydown'

/**
 * @param debugPanelOpen — US-AM-34: when true, Escape closes the debug panel first
 * @param debugFeatureEnabled — **`Ctrl+/`** toggles debug when true
 * @param resetConversationFeatureEnabled — **`Ctrl+r`** opens reset when true
 * @param showResetConfirm — reset dialog focus-trap keys when true
 */
export function useKorvoChatDocumentKeydown(
  ctx: KorvoChatDocumentKeydownContext,
  editingMessageId: string | null,
  debugPanelOpen: boolean,
  debugFeatureEnabled: boolean,
  resetConversationFeatureEnabled: boolean,
  showResetConfirm: boolean,
): void {
  const ctxRef = useRef(ctx)
  ctxRef.current = ctx

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      createKorvoChatDocumentKeydownHandler(ctxRef.current)(e)
    }
    document.addEventListener('keydown', listener)
    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [editingMessageId, debugPanelOpen, debugFeatureEnabled, resetConversationFeatureEnabled, showResetConfirm])
}
